import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  PlusCircle,
  Wallet,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  PieChart as PieChartIcon,
  X
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import DashboardPage from '../../components/DashboardPage';

// Mock data - In a real app, this would come from your API
const mockData = {
  totalBudget: 5000,
  spent: 3250,
  remaining: 1750,
  monthlyData: [
    { day: '1', spent: 150 },
    { day: '5', spent: 420 },
    { day: '10', spent: 980 },
    { day: '15', spent: 1540 },
    { day: '20', spent: 2100 },
    { day: '25', spent: 2800 },
    { day: '30', spent: 3250 },
  ],
  categories: [
    { name: 'Housing', budget: 2000, spent: 1800, color: '#68A511' },
    { name: 'Food', budget: 800, spent: 600, color: '#059669' },
    { name: 'Transport', budget: 400, spent: 350, color: '#0284c7' },
    { name: 'Entertainment', budget: 300, spent: 200, color: '#7c3aed' },
    { name: 'Utilities', budget: 250, spent: 200, color: '#db2777' },
    { name: 'Shopping', budget: 400, spent: 100, color: '#dc2626' },
  ],
  alerts: [
    {
      type: 'warning',
      message: 'Housing category is at 90% of budget'
    },
    {
      type: 'success',
      message: "You're under budget in Shopping by $300"
    }
  ]
};

const Budget = () => {
  const [data, setData] = useState(() => {
    const savedData = localStorage.getItem('budgetData');
    return savedData ? JSON.parse(savedData) : mockData;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newExpense, setNewExpense] = useState({
    category: '',
    amount: '',
    description: ''
  });
  const [activeTimeframe, setActiveTimeframe] = useState('month');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);

  useEffect(() => {
    // Save to localStorage whenever data changes
    localStorage.setItem('budgetData', JSON.stringify(data));
  }, [data]);

  const handleAddExpense = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate input
    if (!newExpense.category || !newExpense.amount || !newExpense.description) {
      alert('Please fill in all fields');
      return;
    }

    const amount = parseFloat(newExpense.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Update the category spending
    const updatedCategories = data.categories.map(cat => {
      if (cat.name === newExpense.category) {
        return {
          ...cat,
          spent: cat.spent + amount
        };
      }
      return cat;
    });

    // Update monthly data
    const today = new Date().getDate().toString();
    const updatedMonthlyData = [...data.monthlyData];
    const lastEntry = updatedMonthlyData[updatedMonthlyData.length - 1];
    if (lastEntry.day === today) {
      lastEntry.spent += amount;
    } else {
      updatedMonthlyData.push({ day: today, spent: lastEntry.spent + amount });
    }

    // Update total spent and remaining
    const newSpent = data.spent + amount;
    const newRemaining = data.totalBudget - newSpent;

    // Update alerts if necessary
    const updatedAlerts = [...data.alerts];
    const category = data.categories.find(cat => cat.name === newExpense.category);
    if (category) {
      const newSpentPercentage = ((category.spent + amount) / category.budget) * 100;
      if (newSpentPercentage >= 90 && !updatedAlerts.some(alert => 
        alert.type === 'warning' && alert.message.includes(category.name)
      )) {
        updatedAlerts.push({
          type: 'warning',
          message: `${category.name} category is at ${newSpentPercentage.toFixed(1)}% of budget`
        });
      }
    }

    setData(prev => ({
      ...prev,
      spent: newSpent,
      remaining: newRemaining,
      categories: updatedCategories,
      monthlyData: updatedMonthlyData,
      alerts: updatedAlerts
    }));

    setShowAddExpense(false);
    setNewExpense({ category: '', amount: '', description: '' });
  };

  const handleDeleteAlert = (index: number) => {
    setData(prev => ({
      ...prev,
      alerts: prev.alerts.filter((_, i) => i !== index)
    }));
  };

  const handleTimeframeChange = (timeframe: string) => {
    setActiveTimeframe(timeframe);
    // In a real app, you would fetch new data based on the timeframe
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleEditCategory = (categoryName: string, newBudget: number) => {
    const updatedCategories = data.categories.map(cat => {
      if (cat.name === categoryName) {
        return {
          ...cat,
          budget: newBudget
        };
      }
      return cat;
    });

    setData(prev => ({
      ...prev,
      categories: updatedCategories
    }));
    setEditingCategory(null);
  };

  const handleResetBudget = () => {
    if (window.confirm('Are you sure you want to reset all budget data?')) {
      localStorage.removeItem('budgetData');
      setData(mockData);
    }
  };

  // Add category click handler for detailed view
  const handleCategoryClick = (category: any) => {
    const monthlySpending = data.monthlyData.map(entry => ({
      day: entry.day,
      spent: (entry.spent / data.spent) * category.spent
    }));

    // Show detailed modal with category spending trend
    setShowCategoryDetail({
      ...category,
      monthlySpending
    });
  };

  const [showCategoryDetail, setShowCategoryDetail] = useState<any>(null);

  const BudgetProgressCard = ({ title, current, total, icon, color }: any) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-2 bg-${color}/10 rounded-lg`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {((current / total) * 100).toFixed(1)}%
        </span>
      </div>
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
        ${current.toLocaleString()}
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
          / ${total.toLocaleString()}
        </span>
      </p>
      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full mt-3">
        <div
          className={`h-2 rounded-full bg-${color}`}
          style={{ width: `${(current / total) * 100}%` }}
        />
      </div>
    </motion.div>
  );

  return (
    <DashboardPage
      title="Budget Tracker"
      subtitle="Track and manage your monthly budget"
    >
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {/* Add Reset Button next to Timeframe Selector */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              {['week', 'month', 'year'].map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => handleTimeframeChange(timeframe)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    activeTimeframe === timeframe
                      ? 'bg-[#68A511] text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
                </button>
              ))}
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleResetBudget}
              className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Reset Budget
            </motion.button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <BudgetProgressCard
              title="Total Budget"
              current={data.spent}
              total={data.totalBudget}
              icon={<Wallet className="w-6 h-6 text-[#68A511]" />}
              color="[#68A511]"
            />
            <BudgetProgressCard
              title="Monthly Spending"
              current={data.spent}
              total={data.totalBudget}
              icon={<TrendingDown className="w-6 h-6 text-rose-500" />}
              color="rose-500"
            />
            <BudgetProgressCard
              title="Remaining Budget"
              current={data.remaining}
              total={data.totalBudget}
              icon={<TrendingUp className="w-6 h-6 text-emerald-500" />}
              color="emerald-500"
            />
          </div>

          {/* Add Expense Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddExpense(true)}
            className="mb-8 flex items-center gap-2 px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] transition-colors"
          >
            <PlusCircle className="w-5 h-5" />
            Add Expense
          </motion.button>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Spending Trend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Monthly Spending Trend
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="spent"
                      stroke="#68A511"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Enhanced Category List with Click Interaction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Budget by Category
              </h3>
              <div className="space-y-4">
                {data.categories.map((category, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => handleCategoryClick(category)}
                    className="flex items-center justify-between cursor-pointer p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {category.name}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        ${category.spent.toLocaleString()}
                        <span className="text-gray-500 dark:text-gray-400">
                          /${category.budget.toLocaleString()}
                        </span>
                      </p>
                      <div className="w-32 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full mt-1">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            backgroundColor: category.color,
                            width: `${(category.spent / category.budget) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Alerts */}
          {data.alerts.length > 0 && (
            <div className="space-y-4 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Budget Alerts
              </h3>
              {data.alerts.map((alert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-lg border flex justify-between items-center ${
                    alert.type === 'warning'
                      ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20'
                      : 'bg-green-50 border-green-200 dark:bg-green-900/20'
                  }`}
                >
                  <div className="flex items-center">
                    <AlertTriangle className={`w-5 h-5 ${
                      alert.type === 'warning' ? 'text-yellow-600' : 'text-green-600'
                    }`} />
                    <p className="ml-3 text-sm text-gray-600 dark:text-gray-300">
                      {alert.message}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteAlert(index)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          {/* Category Detail Modal */}
          {showCategoryDetail && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-2xl w-full"
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {showCategoryDetail.name} Details
                  </h3>
                  <button
                    onClick={() => setShowCategoryDetail(null)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Budget</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        ${showCategoryDetail.budget.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Spent</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        ${showCategoryDetail.spent.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={showCategoryDetail.monthlySpending}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="spent"
                          stroke={showCategoryDetail.color}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {editingCategory === showCategoryDetail.name ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const newBudget = parseFloat(e.currentTarget.budget.value);
                        if (!isNaN(newBudget) && newBudget > 0) {
                          handleEditCategory(showCategoryDetail.name, newBudget);
                        }
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          New Budget Amount
                        </label>
                        <input
                          name="budget"
                          type="number"
                          defaultValue={showCategoryDetail.budget}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                          required
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingCategory(null)}
                          className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e]"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      onClick={() => setEditingCategory(showCategoryDetail.name)}
                      className="w-full px-4 py-2 text-[#68A511] border border-[#68A511] rounded-lg hover:bg-[#68A511]/10 transition-colors"
                    >
                      Edit Budget
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Add Expense Modal */}
          {showAddExpense && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Add New Expense
                </h3>
                <form onSubmit={handleAddExpense} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      required
                    >
                      <option value="">Select Category</option>
                      {data.categories.map((category) => (
                        <option key={category.name} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount
                    </label>
                    <input
                      type="number"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Enter amount"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={newExpense.description}
                      onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Enter description"
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddExpense(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e]"
                    >
                      Add Expense
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </DashboardPage>
  );
};

export default Budget; 